/**
 * Mockup Manager - localStorage Helper
 *
 * Manages room mockup request tracking and result storage for anonymous users.
 * Data persists in localStorage until migrated to user account.
 */

const STORAGE_KEYS = {
  ACTIVE_REQUESTS: 'roomMockup_activeRequests',
  RESULT: 'roomMockup_result',
  LISTING_CONTEXT: 'roomMockup_listingContext',
};

/**
 * Save an active request to localStorage
 * @param {string} jobId - Unique job identifier
 * @param {number} timestamp - Request start timestamp
 */
export const saveRequest = (jobId, timestamp = Date.now()) => {
  try {
    const activeRequests = getActiveRequests();
    activeRequests[jobId] = {
      jobId,
      timestamp,
      status: 'pending',
    };
    localStorage.setItem(STORAGE_KEYS.ACTIVE_REQUESTS, JSON.stringify(activeRequests));
  } catch (error) {
    console.error('[Mockup Manager] Failed to save request:', error);
  }
};

/**
 * Update request status
 * @param {string} jobId - Job identifier
 * @param {string} status - New status
 */
export const updateRequestStatus = (jobId, status) => {
  try {
    const activeRequests = getActiveRequests();
    if (activeRequests[jobId]) {
      activeRequests[jobId].status = status;
      activeRequests[jobId].updatedAt = Date.now();
      localStorage.setItem(STORAGE_KEYS.ACTIVE_REQUESTS, JSON.stringify(activeRequests));
    }
  } catch (error) {
    console.error('[Mockup Manager] Failed to update request status:', error);
  }
};

/**
 * Get all active requests
 * @returns {object} Map of jobId to request data
 */
export const getActiveRequests = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.ACTIVE_REQUESTS);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[Mockup Manager] Failed to get active requests:', error);
    return {};
  }
};

/**
 * Get a specific request
 * @param {string} jobId - Job identifier
 * @returns {object|null} Request data or null
 */
export const getRequest = jobId => {
  const activeRequests = getActiveRequests();
  return activeRequests[jobId] || null;
};

/**
 * Clear a completed or failed request
 * @param {string} jobId - Job identifier
 */
export const clearRequest = jobId => {
  try {
    const activeRequests = getActiveRequests();
    delete activeRequests[jobId];
    localStorage.setItem(STORAGE_KEYS.ACTIVE_REQUESTS, JSON.stringify(activeRequests));
  } catch (error) {
    console.error('[Mockup Manager] Failed to clear request:', error);
  }
};

/**
 * Save mockup result data (only latest result)
 * @param {string} jobId - Job identifier
 * @param {object} resultData - Mockup result data
 */
export const saveResult = (jobId, resultData) => {
  try {
    const result = {
      jobId,
      ...resultData,
      savedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEYS.RESULT, JSON.stringify(result));
  } catch (error) {
    console.error('[Mockup Manager] Failed to save result:', error);
  }
};

/**
 * Get stored result
 * @returns {object|null} Result data or null
 */
export const getResult = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.RESULT);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('[Mockup Manager] Failed to get result:', error);
    return null;
  }
};

/**
 * Check if there are any pending requests
 * @returns {boolean}
 */
export const hasPendingRequests = () => {
  const activeRequests = getActiveRequests();
  return Object.keys(activeRequests).length > 0;
};

/**
 * Get the first pending request (for resuming on page load)
 * @returns {object|null} First pending request or null
 */
export const getFirstPendingRequest = () => {
  const activeRequests = getActiveRequests();
  const jobIds = Object.keys(activeRequests);
  return jobIds.length > 0 ? activeRequests[jobIds[0]] : null;
};

/**
 * Clear all mockup data (used after migration to user account)
 */
export const clearAllData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_REQUESTS);
    localStorage.removeItem(STORAGE_KEYS.RESULT);
    console.log('[Mockup Manager] All data cleared');
  } catch (error) {
    console.error('[Mockup Manager] Failed to clear data:', error);
  }
};

/**
 * Clear result only (keep active requests)
 */
export const clearResult = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.RESULT);
  } catch (error) {
    console.error('[Mockup Manager] Failed to clear result:', error);
  }
};

/**
 * Save listing context for a job
 * @param {string} jobId - Job identifier
 * @param {object} listingContext - Listing context { listingId, listingSlug, listingTitle }
 */
export const saveListingContext = (jobId, listingContext) => {
  try {
    const contexts = getListingContexts();
    contexts[jobId] = listingContext;
    localStorage.setItem(STORAGE_KEYS.LISTING_CONTEXT, JSON.stringify(contexts));
  } catch (error) {
    console.error('[Mockup Manager] Failed to save listing context:', error);
  }
};

/**
 * Get listing context for a job
 * @param {string} jobId - Job identifier
 * @returns {object|null} Listing context or null
 */
export const getListingContext = jobId => {
  try {
    const contexts = getListingContexts();
    return contexts[jobId] || null;
  } catch (error) {
    console.error('[Mockup Manager] Failed to get listing context:', error);
    return null;
  }
};

/**
 * Get all listing contexts
 * @returns {object} Map of jobId to listing context
 */
export const getListingContexts = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.LISTING_CONTEXT);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[Mockup Manager] Failed to get listing contexts:', error);
    return {};
  }
};

/**
 * Clear listing context for a job
 * @param {string} jobId - Job identifier
 */
export const clearListingContext = jobId => {
  try {
    const contexts = getListingContexts();
    delete contexts[jobId];
    localStorage.setItem(STORAGE_KEYS.LISTING_CONTEXT, JSON.stringify(contexts));
  } catch (error) {
    console.error('[Mockup Manager] Failed to clear listing context:', error);
  }
};
